import { useTranslation } from '@shared/core';
import type { DynamicBannerContent } from '@apps/card/components/DynamicBanner/useDynamicBanner.types';
import { useDynamicBanner } from '@apps/card/components/DynamicBanner/useDynamicBanner';
import { POTENTIALLY_FREE_SHIPPING_COUNTRIES, useFreeShippingCountries } from '../routes/Dashboard/components/ProductDetailsPage/useFreeShippingCountries';
import { useEffect, useMemo } from 'react';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { Shipping } from '@withjoy/joykit/icons';

/**
 * Shows the free-shipping banner for the countries the products ship to for free.
 * `hasSettled`: the product list is final. Until then an empty list leaves the banner alone, so a
 * banner the server rendered (`useCatalogFreeShippingBanner`) stays up while products load; once
 * settled, no eligible country hides it.
 */
export const useFreeShippingBanner = (products: StationeryProduct[], hasSettled = true) => {
  const freeShippingCountries = useFreeShippingCountries(products);
  const { showBanner, hideBanner } = useDynamicBanner();
  const { t } = useTranslation('stationery');
  const tBanner = t('dashboard', 'dynamicBanner');

  useEffect(() => {
    if (freeShippingCountries.length > 0) {
      showBanner({
        icon: Shipping,
        message: tBanner.freeShipping({ countries: freeShippingCountries.join(', ') }),
      });
    } else if (hasSettled) {
      hideBanner();
    }
  }, [freeShippingCountries, hasSettled, showBanner, hideBanner, tBanner]);

  // Hidden on unmount only — not on every dependency change, which would take a server-rendered
  // banner down on the first re-render after hydration and put it back once products land.
  useEffect(() => hideBanner, [hideBanner]);
};

/**
 * The banner for the catalog's free-shipping countries, before any products have loaded. A
 * server render can start with it (PRNT-5494): eligibility is a property of the catalog, not the
 * visitor, and the products query is client-only. `useFreeShippingBanner` corrects it once
 * products land.
 */
export const useCatalogFreeShippingBanner = (): DynamicBannerContent => {
  const { t } = useTranslation('stationery');
  const tBanner = t('dashboard', 'dynamicBanner');

  return useMemo(() => ({ icon: Shipping, message: tBanner.freeShipping({ countries: POTENTIALLY_FREE_SHIPPING_COUNTRIES.join(', ') }) }), [tBanner]);
};
